import { app, authentication } from '@microsoft/teams-js';

export type TeamsTheme = 'default' | 'dark' | 'contrast';

let inTeams = false;
let initialized = false;

/**
 * Initialisiert das Teams SDK. Schlägt der Aufruf fehl, läuft die App
 * außerhalb von Teams (normaler Browser) — das ist ein erwarteter Fall.
 */
export async function initTeams(): Promise<{ inTeams: boolean; theme: TeamsTheme }> {
  try {
    await app.initialize();
    const ctx = await app.getContext();
    initialized = true;
    inTeams = true;
    return { inTeams: true, theme: (ctx.app.theme as TeamsTheme) ?? 'default' };
  } catch {
    inTeams = false;
    return { inTeams: false, theme: 'default' };
  }
}

export function isInTeams(): boolean {
  return inTeams;
}

/**
 * Teams-SSO-Token (nur innerhalb von Teams verfügbar). Kann für den
 * On-Behalf-Of-Fluss im Backend genutzt werden — siehe docs/setup.md.
 */
export async function getTeamsToken(): Promise<string | null> {
  if (!inTeams) return null;
  try {
    return await authentication.getAuthToken();
  } catch {
    return null;
  }
}

export function registerThemeChange(cb: (theme: TeamsTheme) => void): void {
  if (initialized) {
    app.registerOnThemeChangeHandler((t) => cb(t as TeamsTheme));
  }
}

/**
 * Anmeldung innerhalb des Teams-Clients. Der normale MSAL-Redirect wird im
 * Teams-Tab (iframe) blockiert; stattdessen öffnet Teams ein verwaltetes Popup
 * (`authentication.authenticate`), das die Anmeldung als Top-Level-Fenster
 * durchführt. Gibt true zurück, wenn die Anmeldung erfolgreich war.
 */
export async function authenticateInTeams(): Promise<boolean> {
  try {
    await app.initialize();
    await authentication.authenticate({
      url: `${window.location.origin}/?teamsauth=1`,
      width: 600,
      height: 535,
    });
    return true;
  } catch {
    return false;
  }
}

/** Öffnet die App im System-Browser (Fallback, falls der Popup-Login scheitert). */
export async function openInBrowser(path = '/'): Promise<void> {
  const url = `${window.location.origin}${path}`;
  try {
    await app.initialize();
    await app.openLink(url);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}
